import { useState } from 'react';
import { Copy, Check } from 'lucide-react';

const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function tokenizeHTML(code) {
  const tokens = [];
  let i = 0;
  while (i < code.length) {
    if (code.startsWith('<!--', i)) {
      const end = code.indexOf('-->', i);
      const ep = end === -1 ? code.length : end + 3;
      tokens.push({ t: 'cm', v: code.slice(i, ep) });
      i = ep;
    } else if (code[i] === '<') {
      const end = code.indexOf('>', i);
      if (end === -1) { tokens.push({ t: 'tx', v: code.slice(i) }); break; }
      parseTag(code.slice(i, end + 1), tokens);
      i = end + 1;
    } else {
      const next = code.indexOf('<', i);
      const ep = next === -1 ? code.length : next;
      tokens.push({ t: 'tx', v: code.slice(i, ep) });
      i = ep;
    }
  }
  return tokens;
}

function parseTag(tag, tokens) {
  let i = 0;
  let open = '<';
  i = 1;
  if (tag[1] === '/') { open = '</'; i = 2; }
  let name = '';
  while (i < tag.length && /[\w!-]/.test(tag[i])) { name += tag[i]; i++; }
  tokens.push({ t: 'tg', v: open + name });

  while (i < tag.length) {
    if (/\s/.test(tag[i])) {
      let ws = '';
      while (i < tag.length && /\s/.test(tag[i])) { ws += tag[i]; i++; }
      tokens.push({ t: 'tx', v: ws });
    } else if (tag[i] === '>' || (tag[i] === '/' && i + 1 < tag.length && tag[i + 1] === '>')) {
      tokens.push({ t: 'tg', v: tag.slice(i) });
      break;
    } else if (/[\w-]/.test(tag[i])) {
      let attr = '';
      while (i < tag.length && /[\w-]/.test(tag[i])) { attr += tag[i]; i++; }
      tokens.push({ t: 'at', v: attr });
      if (i < tag.length && tag[i] === '=') {
        tokens.push({ t: 'tg', v: '=' });
        i++;
        if (i < tag.length && (tag[i] === '"' || tag[i] === "'")) {
          const q = tag[i];
          const end = tag.indexOf(q, i + 1);
          const ep = end === -1 ? tag.length : end + 1;
          tokens.push({ t: 'vl', v: tag.slice(i, ep) });
          i = ep;
        } else {
          let val = '';
          while (i < tag.length && !/[\s>]/.test(tag[i])) { val += tag[i]; i++; }
          tokens.push({ t: 'vl', v: val });
        }
      }
    } else {
      tokens.push({ t: 'tx', v: tag[i] });
      i++;
    }
  }
}

function renderTokens(tokens) {
  const classMap = { tg: 'syn-tg', at: 'syn-at', vl: 'syn-vl', cm: 'syn-cm', pr: 'syn-pr', cv: 'syn-cv' };
  return tokens.map(tk => {
    const escaped = esc(tk.v);
    const cls = classMap[tk.t];
    return cls ? `<span class="${cls}">${escaped}</span>` : escaped;
  }).join('');
}

function highlightCSS(code) {
  const tokens = [];
  const lines = code.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('/*') || trimmed.startsWith('*') || trimmed.endsWith('*/')) {
      tokens.push({ t: 'cm', v: line });
    } else if (trimmed.endsWith('{')) {
      tokens.push({ t: 'tg', v: line });
    } else if (trimmed === '}') {
      tokens.push({ t: 'tg', v: line });
    } else if (line.includes(':') && !trimmed.startsWith('//')) {
      const colonIdx = line.indexOf(':');
      tokens.push({ t: 'pr', v: line.slice(0, colonIdx) });
      tokens.push({ t: 'tg', v: ':' });
      tokens.push({ t: 'cv', v: line.slice(colonIdx + 1) });
    } else {
      tokens.push({ t: 'tx', v: line });
    }
    tokens.push({ t: 'tx', v: '\n' });
  }
  if (tokens.length > 0) tokens.pop();
  return tokens;
}

const CodeBlock = ({ code, lang = 'html', title }) => {
  const [copied, setCopied] = useState(false);
  const tokens = lang === 'css' ? highlightCSS(code) : tokenizeHTML(code);
  const html = renderTokens(tokens);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-4 rounded-xl overflow-hidden border border-white/10" data-testid="code-block">
      <div className="flex items-center justify-between px-4 py-2 bg-[#1a1a2e] border-b border-white/10">
        <span className="text-xs font-mono text-[#A1A1AA] uppercase tracking-wider">
          {title || lang}
        </span>
        <button
          onClick={handleCopy}
          className="text-[#A1A1AA] hover:text-white transition-colors p-1"
          data-testid="copy-code-btn"
        >
          {copied ? <Check size={14} className="text-[#16FF00]" /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="p-4 bg-[#0D0D1A] overflow-x-auto text-sm leading-relaxed" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        <code dangerouslySetInnerHTML={{ __html: html }} />
      </pre>
    </div>
  );
};

export default CodeBlock;
